/**
 * Run-end overlay — supports two outcomes.
 *
 * The original game-over overlay only spoke for the loss case. The
 * "Forge a Star" theme adds a clear victory path, so the overlay now
 * accepts a reason on show() and renders distinct copy + iconography:
 *
 *   - "shattered" → 💥 EARTH SHATTERED · "Restart" button
 *   - "star-forged" → 🌟 STAR FORGED · "Continue (endless)" button
 *
 * The data-testids stay stable so existing smoke tests keep working.
 */

import type { ScoreSnapshot } from "../game/score.ts";
import { formatNumber, t } from "../i18n/index.ts";

export type GameOverReason = "shattered" | "star-forged";

export interface GameOverOverlay {
  element: HTMLDivElement;
  show(snapshot: ScoreSnapshot, reason?: GameOverReason): void;
  hide(): void;
  detach(): void;
}

export interface GameOverOverlayOptions {
  onRestart: () => void;
  /** Optional handler for "Continue" on a victory overlay. Defaults to hide(). */
  onContinue?: () => void;
  /**
   * F-076 — when provided, the overlay renders a 🏆 글로벌 등록 button.
   * Omit (e.g. offline mode) to keep the overlay submit-free.
   */
  onSubmit?: () => void;
}

const COPY_KEYS: Record<
  GameOverReason,
  { title: string; sub: string; cta: string }
> = {
  shattered: {
    title: "gameover.shattered.title",
    sub: "gameover.shattered.sub",
    cta: "gameover.shattered.cta",
  },
  "star-forged": {
    title: "gameover.star.title",
    sub: "gameover.star.sub",
    cta: "gameover.star.cta",
  },
};

function copyFor(reason: GameOverReason): { title: string; sub: string; cta: string } {
  const keys = COPY_KEYS[reason];
  return { title: t(keys.title), sub: t(keys.sub), cta: t(keys.cta) };
}

export function createGameOverOverlay(
  host: HTMLElement,
  options: GameOverOverlayOptions,
): GameOverOverlay {
  const element = document.createElement("div");
  element.dataset["testid"] = "gameover-overlay";
  element.className =
    "pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/75 opacity-0 transition-opacity duration-300";
  element.style.display = "none";

  const initial = copyFor("shattered");

  const title = document.createElement("h2");
  title.dataset["testid"] = "gameover-title";
  title.className = "font-display text-5xl tracking-tight text-cosmic-star drop-shadow-[0_4px_16px_rgba(0,0,0,0.85)]";
  title.textContent = initial.title;

  const sub = document.createElement("p");
  sub.dataset["testid"] = "gameover-sub";
  sub.className = "max-w-md text-center text-sm text-slate-300";
  sub.textContent = initial.sub;

  const scoreLine = document.createElement("p");
  scoreLine.dataset["testid"] = "gameover-score";
  scoreLine.className = "text-lg text-slate-200";
  scoreLine.textContent = t("gameover.final", { value: formatNumber(0) });

  const highLine = document.createElement("p");
  highLine.dataset["testid"] = "gameover-high";
  highLine.className = "text-sm text-slate-400";
  highLine.textContent = t("gameover.high", { value: formatNumber(0) });

  // Most-recently-shown reason — used by the primary button to pick
  // the right callback.
  let currentReason: GameOverReason = "shattered";

  const actions = document.createElement("div");
  actions.dataset["testid"] = "gameover-actions";
  actions.className = "flex flex-col items-center gap-2 sm:flex-row sm:gap-3";

  // Submit button (F-076) — only rendered if onSubmit is wired AND the
  // current snapshot has a positive score (set in show()).
  const submit = document.createElement("button");
  submit.type = "button";
  submit.dataset["testid"] = "gameover-submit";
  submit.className =
    "pointer-events-auto rounded-full bg-white/10 px-5 py-2 font-display text-sm text-cosmic-glow ring-1 ring-cosmic-glow/45 transition hover:bg-white/20";
  submit.textContent = t("gameover.submit");
  submit.style.display = "none";
  submit.addEventListener("click", () => {
    options.onSubmit?.();
  });

  const primary = document.createElement("button");
  primary.type = "button";
  primary.dataset["testid"] = "gameover-restart";
  primary.className =
    "pointer-events-auto rounded-full bg-cosmic-glow/85 px-6 py-2 font-display text-cosmic-deep transition hover:bg-cosmic-glow";
  primary.textContent = initial.cta;
  primary.addEventListener("click", () => {
    if (currentReason === "star-forged") {
      if (options.onContinue) options.onContinue();
      else hide();
    } else {
      options.onRestart();
    }
  });

  actions.append(submit, primary);
  element.append(title, sub, scoreLine, highLine, actions);
  host.appendChild(element);

  const show = (
    snapshot: ScoreSnapshot,
    reason: GameOverReason = "shattered",
  ): void => {
    currentReason = reason;
    const copy = copyFor(reason);
    title.textContent = copy.title;
    sub.textContent = copy.sub;
    primary.textContent = copy.cta;
    scoreLine.textContent = t("gameover.final", {
      value: formatNumber(snapshot.current),
    });
    highLine.textContent = t("gameover.high", {
      value: formatNumber(snapshot.highScore),
    });
    // F-076 — submit visible only when wire-up AND a non-zero score.
    submit.textContent = t("gameover.submit");
    submit.style.display =
      options.onSubmit && snapshot.current > 0 ? "block" : "none";
    element.style.display = "flex";
    void element.offsetWidth;
    element.style.opacity = "1";
    element.style.pointerEvents = "auto";
  };

  const hide = (): void => {
    element.style.opacity = "0";
    element.style.pointerEvents = "none";
    element.style.display = "none";
  };

  const detach = (): void => {
    element.remove();
  };

  return { element, show, hide, detach };
}
